import { useTranslation } from 'react-i18next'

import { TimeTrackingTab } from '../const'
import { InternalFilterState } from './filterDropdownState'
import { FilterSelectField } from './FilterSelectField'
import { Button } from '@/Atomic/Molecules/Button'

interface FilterOption {
  label: string
  value: string
}

interface FilterDropdownBodyProps {
  currentTab: TimeTrackingTab
  canFilterByTeam?: boolean
  state: InternalFilterState
  statusOptions: FilterOption[]
  teamOptions: FilterOption[]
  teamMemberOptions: FilterOption[]
  customerOptions: FilterOption[]
  orderOptions: FilterOption[]
  workPackageOptions: FilterOption[]
  onChange: (key: keyof InternalFilterState, value: string | null) => void
  onApply: () => void
  onClear: () => void
}

export const FilterDropdownBody = ({
  currentTab,
  canFilterByTeam = true,
  state,
  statusOptions,
  teamOptions,
  teamMemberOptions,
  customerOptions,
  orderOptions,
  workPackageOptions,
  onChange,
  onApply,
  onClear,
}: FilterDropdownBodyProps) => {
  const { t } = useTranslation()
  const isMyTeam = currentTab === TimeTrackingTab.MY_TEAM
  const isMyTimes = currentTab === TimeTrackingTab.MY_TIMES

  return (
    <div className="time-tracking-filter-content">
      <h5 className="time-tracking-filter-title">
        {t('timeTracking.filters')}
      </h5>

      {!isMyTeam && (
        <FilterSelectField
          label={t('timeTracking.filterLabels.status')}
          keyPrefix="status"
          options={statusOptions}
          value={state.status}
          placeholder={t('timeTracking.filterPlaceholders.status')}
          isDisabled={isMyTimes}
          onChange={(v) => onChange('status', v)}
        />
      )}

      <FilterSelectField
        label={t('timeTracking.filterLabels.teamLeader')}
        keyPrefix="teamLeader"
        options={teamOptions}
        value={state.teamLeader}
        placeholder={t('timeTracking.filterPlaceholders.teamLeader')}
        isDisabled={isMyTimes || !canFilterByTeam}
        onChange={(v) => onChange('teamLeader', v)}
      />

      {!isMyTeam && (
        <FilterSelectField
          label={t('timeTracking.filterLabels.teamMember')}
          keyPrefix="teamMember"
          options={teamMemberOptions}
          value={state.teamMember}
          placeholder={t('timeTracking.filterPlaceholders.teamMember')}
          isDisabled={
            isMyTimes ||
            (!state.teamLeader && !state.teamMember) ||
            !canFilterByTeam
          }
          onChange={(v) => onChange('teamMember', v)}
          extraKey={state.teamLeader || 'none'}
        />
      )}

      {!isMyTeam && (
        <FilterSelectField
          label={t('timeTracking.filterLabels.customer')}
          keyPrefix="customer"
          options={customerOptions}
          value={state.customer}
          placeholder={t('timeTracking.filterPlaceholders.customer')}
          onChange={(v) => onChange('customer', v)}
        />
      )}

      {!isMyTeam && (
        <FilterSelectField
          label={t('timeTracking.filterLabels.order')}
          keyPrefix="order"
          options={orderOptions}
          value={state.order}
          placeholder={t('timeTracking.filterPlaceholders.order')}
          isDisabled={!state.customer}
          onChange={(v) => onChange('order', v)}
        />
      )}

      {!isMyTeam && (
        <FilterSelectField
          label={t('timeTracking.form.workPackage')}
          keyPrefix="workPackage"
          options={workPackageOptions}
          value={state.workPackage}
          placeholder={t('timeTracking.form.selectWorkPackage')}
          isDisabled={!state.customer || !state.order}
          onChange={(v) => onChange('workPackage', v)}
          extraKey={`${state.order || 'empty'}-${workPackageOptions.length}`}
        />
      )}

      <div className="time-tracking-filter-footer">
        <Button
          variant="outlined"
          title={t('timeTracking.clearFilters')}
          size="sm"
          onClick={onClear}
        />
        <Button
          variant="primary"
          title={t('timeTracking.applyFilters')}
          size="sm"
          onClick={onApply}
        />
      </div>
    </div>
  )
}
